import React from "react";
import { Box } from "@mui/material";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import { useSchedulingData } from "../../../new-reports/hooks/useSchedulingData";
import SchedulingTable from "../../../new-reports/components/common/SchedulingTable";
import { SchedulingService } from "../../../new-reports/types/newReportTypes";

type SchedulingContentProps = {
  schedule: SchedulingService[];
  labels: {
    weeklySchedule: string;
    quarterlySchedule: string;
    serviceName: string;
    week: string;
    quarter: string;
    serviceTypes: {
      [key: number]: string;
    };
  };
};

/**
 * UI-friendly scheduling content component (non-PDF)
 * Uses larger fonts suitable for web UI display
 * No pagination - shows all content at once
 */
const SchedulingContent: React.FC<SchedulingContentProps> = ({
  schedule,
  labels,
}) => {
  const theme: Theme = useTheme();

  // Use the shared hook to process data
  const { weeklyGroups, quarterlyGroups } = useSchedulingData({
    schedule,
    serviceTypeLabels: labels.serviceTypes,
  });

  // UI-specific style overrides (larger fonts for web)
  const uiStyleOverrides = {
    tableHeader: {
      fontSize: "14px",
      lineHeight: "14px",
      color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    },
    headerText: {
      fontSize: "14px",
      lineHeight: "20px",
    },
    headerPeriodCell: {
      width: "150px",
    },
    headerPeriodLabel: {
      fontSize: "12px",
      lineHeight: "16px",
    },
    headerPeriodNumber: {
      fontSize: "14px",
      lineHeight: "20px",
    },
    categoryCell: {
      width: "200px",
    },
    categoryText: {
      fontSize: "12px",
      lineHeight: "18px",
    },
    serviceName: {
      fontSize: "12px",
      lineHeight: "18px",
    },
    valueCell: {
      width: "150px",
    },
    valueText: {
      fontSize: "12px",
      lineHeight: "18px",
    },
  };

  return (
    <Box sx={styles.container}>
      {/* Weekly Schedule Section */}
      {weeklyGroups.length > 0 && (
        <Box sx={styles.section}>
          <Box
            component="p"
            sx={{
              ...styles.sectionTitle(theme),
              ...uiStyleOverrides.tableHeader,
            }}
          >
            {labels.weeklySchedule}
          </Box>
          <SchedulingTable
            groups={weeklyGroups}
            isWeekly={true}
            labels={{
              serviceName: labels.serviceName,
              week: labels.week,
              quarter: labels.quarter,
            }}
            styleOverrides={uiStyleOverrides}
          />
        </Box>
      )}

      {/* Quarterly Schedule Section */}
      {quarterlyGroups.length > 0 && (
        <Box sx={styles.section}>
          <Box component="p" sx={styles.sectionTitle(theme)}>
            {labels.quarterlySchedule}
          </Box>
          <SchedulingTable
            groups={quarterlyGroups}
            isWeekly={false}
            labels={{
              serviceName: labels.serviceName,
              week: labels.week,
              quarter: labels.quarter,
            }}
            styleOverrides={uiStyleOverrides}
          />
        </Box>
      )}

      {/* Empty state */}
      {weeklyGroups.length === 0 && quarterlyGroups.length === 0 && (
        <Box sx={styles.emptyState(theme)}>No scheduled services</Box>
      )}
    </Box>
  );
};

export default SchedulingContent;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
    width: "100%",
  },
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
  },
  sectionTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "16px",
    lineHeight: "24px",
    margin: 0,
  }),
  emptyState: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
    textAlign: "center" as "center",
    padding: "32px",
  }),
};
